import {effect, Injectable, NgZone} from '@angular/core';
import {ModalService} from '../modal.service';
import {Howl} from 'howler';
import {AudioStateService} from './audio-state.service';
import {AudioPlayerService} from './audio-player.service';
import {AudioMobileService} from './audio-mobile.service';
import {AudioEffectsService} from './audio-effects.service';
import {GamingAudioPlayerService} from './gaming-audio-player.service';
import {ZKeyDetectorService} from '../z-key-detector.service';

/**
 * Interface for audio track information
 */
export interface AudioTrack {
  url: string;
  title: string;
  artist: string;
  startTime?: number;
  endTime?: number;
  briefDescription?: string;
}

/**
 * Service responsible for audio playback management
 * Implements signal-based state management for Angular 19
 * Uses Howler.js for audio playback
 *
 * REFACTORED: Now acts as a facade that delegates to specialized services
 * while preserving exact public interface and functionality
 */
@Injectable({
  providedIn: 'root'
})
export class AudioService {

  // ============================================================================
  // PUBLIC INTERFACE - EXACT delegation to preserve all functionality
  // ============================================================================

  private volumeUpdateTimeout: any = null;

  constructor(
    private ngZone: NgZone,
    private modalService: ModalService,
    private stateService: AudioStateService,
    private playerService: AudioPlayerService,
    private mobileService: AudioMobileService,
    private effectsService: AudioEffectsService,
    private gamingPlayerService: GamingAudioPlayerService,
    private zKeyDetectorService: ZKeyDetectorService
  ) {
    // CRITICAL: Set up cross-service dependencies
    this.effectsService.setPlayerService(this.playerService);

    // CRITICAL: Load volume from localStorage - EXACT preservation
    this.loadVolumeFromLocalStorage();

    // CRITICAL: Volume effect with less aggressive verification to prevent jitter
    effect(() => {
      const volume = this.stateService.volume();
      this.saveVolumeToLocalStorage(volume);

      // Apply volume to current howl if it exists
      const sound = this.getHowlSound(); // Use the method that checks gaming mode
      if (sound) {
        // Apply volume directly without reset to prevent audio jitter
        sound.volume(volume);

        // Single verification attempt with longer delay to reduce conflicts
        // Only for normal player - gaming player doesn't need effects service
        if (!this.isGamingMode()) {
          setTimeout(() => {
            this.effectsService.verifyVolumeIsApplied(sound, volume, 1);
          }, 100); // Longer delay to prevent conflicts during volume changes
        }
      }
    });
  }

  get tracks() {
    return this.stateService.tracks;
  }

  get currentTrack() {
    return this.stateService.currentTrack;
  }

  get songTitle() {
    return this.stateService.songTitle;
  }

  get artistName() {
    return this.stateService.artistName;
  }

  get startTime() {
    return this.stateService.startTime;
  }

  get endTime() {
    return this.stateService.endTime;
  }

  get briefDescription() {
    return this.stateService.briefDescription;
  }

  get currentTrackIndex() {
    return this.stateService.currentTrackIndex;
  }

  get isPlaying() {
    return this.stateService.isPlaying;
  }

  get isLoading() {
    return this.stateService.isLoading;
  }

  get volume() {
    return this.stateService.volume;
  }

  get currentTime() {
    return this.stateService.currentTime;
  }

  get duration() {
    return this.stateService.duration;
  }

  get isMobile() {
    return this.mobileService.isMobile;
  }

  get isAutoplayBlocked() {
    return this.stateService.isAutoplayBlocked;
  }

  /**
   * Get diagnostic information about current audio state
   * Useful for debugging song transition issues
   */
  getDiagnosticInfo(): any {
    const sound = this.playerService.getHowlSound();
    return {
      hasSound: !!sound,
      isPlaying: this.stateService.isPlaying(),
      isLoading: this.stateService.isLoading(),
      isChangingTrack: this.playerService.getIsChangingTrack(),
      isCreatingHowl: this.playerService.getIsCreatingHowl(),
      currentTrackIndex: this.stateService.currentTrackIndex(),
      tracksCount: this.stateService.tracks().length,
      volume: this.stateService.volume(),
      currentTime: this.stateService.currentTime(),
      duration: this.stateService.duration()
    };
  }

  /**
   * Force cleanup of current audio (for troubleshooting)
   * @param isTrackChange Whether this is a track change cleanup
   */
  forceCleanup(isTrackChange: boolean = true): void {
    console.log('Force cleanup requested', {isTrackChange});
    this.playerService.cleanup(isTrackChange);
  }

  /**
   * Check if the audio service is ready for playback
   * Helps prevent race conditions during initialization
   */
  isReadyForPlayback(): boolean {
    const tracks = this.stateService.tracks();
    const isLoading = this.stateService.isLoading();
    const isChangingTrack = this.playerService.getIsChangingTrack();
    const isCreatingHowl = this.playerService.getIsCreatingHowl();

    return tracks.length > 0 && !isLoading && !isChangingTrack && !isCreatingHowl;
  }

  /**
   * Set the audio tracks and reset the player
   * @param tracks Array of audio tracks
   */
  setTracks(tracks: AudioTrack[]): void {
    console.log('Setting audio tracks:', tracks.length);
    // Always do a session-end cleanup before priming new cache — this also
    // unloads any active Howl from the prior result set.
    this.playerService.cleanup(false);
    this.stateService.setTracks(tracks);
    this.playerService.primeCache(tracks);
    console.log('Audio tracks set; cache primed');
  }

  /**
   * Play a specific track by index
   * @param index Index of the track to play
   * @param isLooping Optional flag to indicate if this is a looping operation
   * @param userInitiated Whether this play action was directly initiated by a user interaction
   */
  playTrack(index: number, isLooping: boolean = false, userInitiated: boolean = false): void {
    if (this.isGamingMode()) {
      console.log('Using gaming mode player');
      this.gamingPlayerService.playTrack(index);
    } else {
      console.log('Using normal mode player');
      this.playerService.playTrack(index, isLooping, userInitiated);
    }
  }

  /**
   * Loop the current track (for slideshow continuation)
   */
  loopCurrentTrack(): boolean {
    const currentIndex = this.stateService.currentTrackIndex();
    this.playTrack(currentIndex, true, false);
    return true;
  }

  /**
   * Seamlessly loop the current track without recreating the Howl instance
   * This provides clean, immediate looping without the "blurry" sound
   */
  loopCurrentTrackSeamlessly(): boolean {
    const sound = this.playerService.getHowlSound();
    const currentTrack = this.stateService.currentTrack();

    if (!sound || !currentTrack) {
      console.warn('Cannot seamlessly loop - no audio or track available');
      return false;
    }

    console.log('Seamlessly looping current track');

    // Seek back to start time without stopping or recreating the audio
    const startTime = currentTrack.startTime || 0;
    sound.seek(startTime);
    this.stateService.setCurrentTime(startTime);

    // Ensure audio is playing
    if (!this.stateService.isPlaying()) {
      this.stateService.setIsPlaying(true);
      sound.play();
    }

    return true;
  }

  /**
   * Toggle play/pause state
   */
  togglePlayPause(): void {
    if (this.isGamingMode()) {
      this.gamingPlayerService.togglePlayPause();
    } else {
      this.playerService.togglePlayPause();
    }
  }

  /**
   * Play the previous track in the playlist
   */
  playPreviousTrack(): void {
    const tracks = this.stateService.tracks();
    const currentIndex = this.stateService.currentTrackIndex();
    const previousIndex = currentIndex > 0 ? currentIndex - 1 : tracks.length - 1;
    this.playTrack(previousIndex, false, true);
  }

  /**
   * Play the next track in the playlist
   */
  playNextTrack(): void {
    const tracks = this.stateService.tracks();
    const currentIndex = this.stateService.currentTrackIndex();
    const nextIndex = (currentIndex + 1) % tracks.length;
    this.playTrack(nextIndex, false, true);
  }

  /**
   * Update the volume level with debouncing to prevent jitter
   * @param volume Volume level (0-1)
   */
  updateVolume(volume: number): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));

    // Clear any pending volume update
    if (this.volumeUpdateTimeout) {
      clearTimeout(this.volumeUpdateTimeout);
    }

    // Apply volume immediately for responsive UI
    const sound = this.playerService.getHowlSound();
    if (sound) {
      sound.volume(clampedVolume);
    }

    // Debounce the state update to prevent excessive effect triggers
    this.volumeUpdateTimeout = setTimeout(() => {
      this.stateService.setVolume(clampedVolume);
      this.volumeUpdateTimeout = null;
    }, 50); // 50ms debounce
  }

  /**
   * Format time in seconds to MM:SS or HH:MM:SS format
   * @param seconds Time in seconds
   */
  formatTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    }
  }

  /**
   * Stop audio playback
   * @param immediate Whether to stop immediately or fade out
   */
  stop(immediate: boolean = false): void {
    if (this.isGamingMode()) {
      this.gamingPlayerService.stop();
    } else {
      this.playerService.stop(immediate);
    }
    this.stateService.setIsPlaying(false);
    this.stateService.setCurrentTime(0);
  }

  /**
   * Clean up audio resources
   */
  cleanup(): void {
    // Clear any pending volume updates
    if (this.volumeUpdateTimeout) {
      clearTimeout(this.volumeUpdateTimeout);
      this.volumeUpdateTimeout = null;
    }

    // Clean up both players
    this.playerService.cleanup();
    this.gamingPlayerService.cleanup();
  }

  /**
   * Get the current Howl sound instance
   */
  getHowlSound(): Howl | null {
    if (this.isGamingMode()) {
      return this.gamingPlayerService.getHowlSound();
    } else {
      return this.playerService.getHowlSound();
    }
  }

  // ============================================================================
  // GAMING MODE DETECTION
  // ============================================================================

  /**
   * Check if currently in gaming mode
   */
  private isGamingMode(): boolean {
    return this.zKeyDetectorService.isGamingMode();
  }

  // ============================================================================
  // LOCALSTORAGE VOLUME MANAGEMENT - EXACT preservation from original
  // ============================================================================

  /**
   * Load volume from localStorage
   * EXACT preservation from original AudioService lines 490-512
   */
  private loadVolumeFromLocalStorage(): void {
    try {
      const savedVolume = localStorage.getItem('audioVolume');
      if (savedVolume !== null) {
        const volume = parseFloat(savedVolume);
        if (!isNaN(volume) && volume >= 0 && volume <= 1) {
          this.stateService.setVolume(volume);
          console.log(`Loaded volume from localStorage: ${volume}`);
        }
      }
    } catch (error) {
      console.warn('Failed to load volume from localStorage:', error);
    }
  }

  /**
   * Save volume to localStorage
   * EXACT preservation from original AudioService lines 505-512
   */
  private saveVolumeToLocalStorage(volume: number): void {
    try {
      localStorage.setItem('audioVolume', volume.toString());
    } catch (error) {
      console.warn('Failed to save volume to localStorage:', error);
    }
  }
}
